import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import * as _ from 'lodash';
import {
  BehaviorSubject,
  debounceTime,
  Observable,
  ReplaySubject,
  Subject,
  Subscription,
} from 'rxjs';
import type { Inbox } from '../../server/models/inbox.model';
import { type MessageSeen } from '../../server/models/message-seen.model';
import * as TaskLeave from '../../server/routes/tasks/leave';
import * as TaskMessagesGet from '../../server/routes/tasks/messages/get';
import * as TaskMessageSend from '../../server/routes/tasks/messages/post';
import * as TaskMessageSeenPost from '../../server/routes/tasks/messages/seen/post';
import * as ThreadDelete from '../../server/routes/threads/delete';
import * as ThreadLeave from '../../server/routes/threads/leave';
import * as ThreadMessagesGet from '../../server/routes/threads/messages/get';
import * as ThreadMessageSend from '../../server/routes/threads/messages/post';
import * as ThreadMessageSeenPost from '../../server/routes/threads/messages/seen/post';
import * as ThreadPatch from '../../server/routes/threads/patch';
import * as ThreadCreate from '../../server/routes/threads/post';
import {
  determineDirectUserId,
  directMessageId,
  generateIdFromOrgId,
  generatePath,
  isLikelyDirectId,
  spaceChannelTitle,
} from '../../server/services/utils.service';
import type { FileElement } from '../../server/types/file-types';
import type {
  Message,
  Thread,
  ThreadOrTaskMode,
  TitleUpdate,
  UserUpdate,
} from '../../server/types/message-types';
import type { OrgTask, OrgTaskFrontEnd } from '../../server/types/task-types';
import type { DeepReadonly } from '../../server/util-type';
import { AuthUserService, type UserOrgObj } from './auth-user.service';
import { TaskService } from './task.service';
import { WebSocketService } from './web-socket.service';

export const messageErrors = [
  'messageIdFailedValidation',
  'messageValidationError',
] as const;

export type MessageErrorType = (typeof messageErrors)[number];

export type MessageSubmitted = Message & {
  salt: string;
};

export type MessageOrMessageSubmitted =
  | (Message & {
      awaitingServerResponse?: undefined;
    })
  | (MessageSubmitted & { awaitingServerResponse: true });

export type DisplayMessage = {
  divId: string;
  visible: boolean;
  isIntersecting: boolean;
  message?: MessageOrMessageSubmitted;
  previousMessage: BehaviorSubject<Message | undefined>;
  messageObservable?: Observable<Message>;
};

export interface DisplayFile {
  tempFileRef: string;
  file: File; // JS file
  fileId?: string;
  progressObservable: ReplaySubject<number>;
  messageFileObservable: Subject<FileElement>;
  statusObservable: Subject<'cancel' | 'remove'>;
}

export class ThreadItem {
  http: HttpClient;
  authUserService: AuthUserService;
  taskService: TaskService;
  messageService: MessageService;

  authUserId: string;
  orgId: string;
  refId: string;
  mode: ThreadOrTaskMode;
  isNewThread = false;
  salt: string | undefined;

  thread?: Thread;
  task?: OrgTaskFrontEnd;
  title?: string;

  messageQueue: {
    messageSubmitted: MessageSubmitted;
    messageObservable: Observable<Message>;
  }[] = [];
  messages: MessageOrMessageSubmitted[] = [];
  messageSeens: MessageSeen[] = [];

  messageNumber = 0;
  unreadMessageCount = 0;
  latestMessage: undefined | MessageOrMessageSubmitted;
  private messageNumberSeen = 0; // nothing seen
  private messageSeenAuthUser: MessageSeen | undefined;

  private liveMessageSubject = new Subject<{
    messages: Message[];
    oldMessageNumber: number;
    messageNumber: number;
  }>();
  liveMessageObservable = this.liveMessageSubject.asObservable();

  private latestMessageSubject = new ReplaySubject<MessageOrMessageSubmitted>();
  latestMessageObservable = this.latestMessageSubject.asObservable();

  private messageSeensReplaySubject = new ReplaySubject<MessageSeen[]>(1);
  messageSeensReplayObservable = this.messageSeensReplaySubject.asObservable();

  private messageNumberSeenReplaySubject = new ReplaySubject<number>(1);
  messageNumberSeenReplayObservable =
    this.messageNumberSeenReplaySubject.asObservable();

  private postMessageNumberSeenSubject = new Subject<number>();

  private titleReplaySubject = new ReplaySubject<string>(1);
  titleReplayObservable = this.titleReplaySubject.asObservable();

  private titleUpdateSubject = new Subject<TitleUpdate & { index: number }>();
  titleUpdateObservable = this.titleUpdateSubject.asObservable();

  private userIdsReplaySubject = new ReplaySubject<string[]>(1);
  userIdsReplayObservable = this.userIdsReplaySubject.asObservable();

  private userUpdateSubject = new Subject<UserUpdate & { index: number }>();
  userUpdateObservable = this.userUpdateSubject.asObservable();

  private unreadMessageCountReplaySubject = new ReplaySubject<number>(1);
  unreadMessageCountReplayObservable =
    this.unreadMessageCountReplaySubject.asObservable();

  private closedSubject = new ReplaySubject<boolean>(1);
  closedObservable = this.closedSubject.asObservable();

  draft: {
    title?: string;
    userIds?: string[];
    body: string;
    files: FileElement[];
  };

  subscriptions = new Subscription();

  constructor(
    {
      authUserId,
      orgId,
      refId,
      mode,
    }: { authUserId: string; orgId: string } & (
      | { refId: string; mode: ThreadOrTaskMode }
      | { refId?: undefined; mode: 'thread' }
      | { refId?: undefined; mode: 'channel' }
    ),
    services: {
      http: HttpClient;
      authUserService: AuthUserService;
      taskService: TaskService;
      messageService: MessageService;
    }
  ) {
    this.authUserId = authUserId;
    this.orgId = orgId;
    this.mode = mode;

    this.http = services.http;
    this.authUserService = services.authUserService;
    this.taskService = services.taskService;
    this.messageService = services.messageService;

    if (refId) {
      this.refId = refId;
    } else {
      this.isNewThread = true;
      const { salt, _id } = generateIdFromOrgId(orgId);
      this.refId = _id;
      this.salt = salt;
    }

    this.unreadMessageCountReplaySubject.next(0);

    const sub = this.postMessageNumberSeenSubject
      .pipe(debounceTime(1000))
      .subscribe((messageNumber) => this.postSeenToServer({ messageNumber }));
    this.subscriptions.add(sub);

    if (this.mode === 'task') {
      const taskSub = this.taskService
        .taskUpdates({ taskId: this.refId })
        .subscribe((task) => this.update({ task }));
      this.subscriptions.add(taskSub);
    }

    if (
      this.mode === 'direct' &&
      isLikelyDirectId({ userId: this.authUserId, threadId: this.refId })
    ) {
      const { org } = this.authUserService.getAuthUserOrgObj({ orgId });
      const otherUserId = determineDirectUserId({
        userId: this.authUserId,
        userIds: org.users.map((u) => u._id),
        threadId: this.refId,
      });
      if (otherUserId) {
        // Generate direct message Thread on the front end if no message has been sent yet
        const directThread: Thread = {
          _id: this.refId,
          orgId,
          userIds: [this.authUserId, otherUserId],
          userUpdates: [],
          messageNumber: 0,
          title: '',
          titleUpdates: [],
          mode: 'direct',
          createdBy: this.authUserId,
          createdAt: 0,
          updatedAt: 0,
        };

        this.update({ thread: directThread });
      }
    }
  }

  update({
    task,
    thread,
    messages = [],
    messageSeens,
  }: (
    | {
        task: OrgTaskFrontEnd;
        thread?: undefined;
      }
    | {
        task?: undefined;
        thread: Thread;
      }
  ) & {
    messages?: Message[];
    messageSeens?: MessageSeen[];
  }) {
    this.isNewThread = false;

    let setLatestMessage = false;

    const oldMessageNumber = this.messageNumber;

    if (thread) {
      if (!this.thread || thread.updatedAt > this.thread.updatedAt) {
        const oldThread = _.cloneDeep(this.thread);
        this.thread = _.cloneDeep(thread);
        if (thread.mode === 'direct') {
          const otherUser = this.authUserService
            .getAuthUserOrgObj({})
            .org.users.find(
              (u) =>
                u._id !== this.authUserId &&
                directMessageId(u._id, this.authUserId) === thread._id
            );
          if (otherUser && this.title !== otherUser.name) {
            this.title = '@' + otherUser.name;
            this.titleReplaySubject.next(this.title);
          }
        } else if (thread.mode === 'space') {
          const space = this.authUserService.getSpace({
            orgId: thread.orgId,
            spaceId: thread._id,
          });
          const spaceName = space
            ? spaceChannelTitle(space.name)
            : 'Unknown space';
          if (this.title !== spaceName) {
            this.title = spaceName;
            this.titleReplaySubject.next(spaceName);
          }
        } else if (this.title !== thread.title) {
          this.title = thread.title;
          this.titleReplaySubject.next(this.title);
        }

        const oldTitleUpdatesLength = oldThread?.titleUpdates.length ?? 0;
        const newTitleUpdates = _.cloneDeep(this.thread.titleUpdates)
          .map((u, index) => ({ ...u, index }))
          .filter((u) => u.index >= oldTitleUpdatesLength);
        newTitleUpdates.forEach((x) => this.titleUpdateSubject.next(x));

        if (!oldThread || !_.isEqual(oldThread.userIds, thread.userIds)) {
          this.userIdsReplaySubject.next(thread.userIds);
        }

        if (thread.messageNumber > this.messageNumber) {
          this.messageNumber = thread.messageNumber;
          setLatestMessage = true;
        }

        const oldUserUpdatesLength = oldThread?.userUpdates.length ?? 0;
        const newUserUpdates = _.cloneDeep(this.thread.userUpdates)
          .map((u, index) => ({ ...u, index }))
          .filter((u) => u.index >= oldUserUpdatesLength);
        newUserUpdates.forEach((x) => this.userUpdateSubject.next(x));

        this.closedSubject.next(!!thread.closedAt);

        this.messageService.threadSubject.next(thread);
      }
    } else if (task) {
      const oldTask = _.cloneDeep(this.task);
      this.task = _.cloneDeep(task);

      if (this.title !== task.title) {
        this.title = task.title;
        this.titleReplaySubject.next(this.title);
      }

      // We need to add userId to the front of the userId array if it is not
      // in the userIds array
      let oldTaskUserIds: string[] | undefined = undefined;
      if (oldTask) {
        oldTaskUserIds = [...oldTask.userIds];
        if (
          oldTask.assignedTo &&
          !oldTaskUserIds.find((u) => u === oldTask.assignedTo)
        )
          oldTaskUserIds.unshift(oldTask.assignedTo);
      }
      const taskUserIds = [...task.userIds];
      if (task.assignedTo && !taskUserIds.find((u) => u === task.assignedTo))
        taskUserIds.unshift(task.assignedTo);
      if (!oldTaskUserIds || !_.isEqual(oldTaskUserIds, taskUserIds)) {
        this.userIdsReplaySubject.next(taskUserIds);
      }

      if (task.messageNumber > this.messageNumber) {
        this.messageNumber = task.messageNumber;
        setLatestMessage = true;
      }

      const oldUserUpdatesLength = oldTask?.userUpdates.length ?? 0;
      const newUserUpdates = _.cloneDeep(this.task.userUpdates)
        .map((u, index) => ({ ...u, index }))
        .filter((u) => u.index >= oldUserUpdatesLength);
      newUserUpdates.forEach((x) => this.userUpdateSubject.next(x));
    } else {
      throw new Error('task or thread should be set');
    }

    if (messages?.length)
      this.updateMessages({
        messages,
        oldMessageNumber,
        messageNumber: this.messageNumber,
      });

    if (!this.latestMessage || setLatestMessage) {
      const latest = this.messages.find((m) => m.number === this.messageNumber);
      if (latest) {
        this.latestMessage = latest;
        this.latestMessageSubject.next(latest);
      }
    }

    if (messageSeens) this.updateMessageSeens(messageSeens);
  }

  private updateMessages({
    messages,
    oldMessageNumber,
    messageNumber,
  }: {
    messages: Message[];
    oldMessageNumber: number;
    messageNumber: number;
  }) {
    const newMessages: Message[] = [];
    messages?.forEach((message) => {
      const index = this.messages.findIndex((m) => m._id === message._id);
      if (index >= 0) {
        // replace
        this.messages[index] = message;
      } else {
        newMessages.push(_.cloneDeep(message));
        this.messages.push(_.cloneDeep(message));

        // If an unsent message has this messageNumber, then we push it to the
        // next available number #pushDownNew
        const submittedMessageIndex = this.messages.findIndex(
          (m) => m.number === message.number && m._id !== message._id
        );
        if (submittedMessageIndex >= 0) {
          const maxExisting = Math.max(...this.messages.map((m) => m.number));
          const newNumber =
            (messageNumber > maxExisting ? messageNumber : maxExisting) + 1;
          const submitted = this.messages[submittedMessageIndex];
          submitted.number = newNumber;
          this.messages.splice(submittedMessageIndex, 1);
          this.messages.push(submitted);
        }
      }
    });

    if (newMessages.length) {
      this.messages.sort((a, b) => a.number - b.number);

      // Send live messages

      newMessages.sort((a, b) => a.number - b.number);
      this.liveMessageSubject.next({
        messages: newMessages,
        oldMessageNumber,
        messageNumber,
      });
    }
  }

  private updateUnreadMessageCount() {
    const threadOrTask = this.thread ?? this.task;
    if (!threadOrTask) return;

    this.unreadMessageCount =
      threadOrTask.messageNumber - this.messageNumberSeen; // we use this.messageNumberSeen NOT this.messageSeenAuthUser because #setRoughMessageNumberSeenInSetSeen
    if (this.unreadMessageCount < 0) {
      console.error('unreadMessageCount was negative');
      this.unreadMessageCount = 0;
    }
    this.unreadMessageCountReplaySubject.next(this.unreadMessageCount);
  }

  updateMessageSeens(messageSeens: MessageSeen[]): void {
    messageSeens.forEach((messageSeen) => {
      const index = this.messageSeens.findIndex(
        (x) => x.userId === messageSeen.userId
      );
      if (index !== -1) {
        this.messageSeens.splice(index, 1, messageSeen);
      } else {
        this.messageSeens.push(messageSeen);
      }

      // Update latest seen replay subject
      if (messageSeen.userId === this.authUserId) {
        if (
          !this.messageSeenAuthUser ||
          this.messageSeenAuthUser.updatedAt < messageSeen.updatedAt
        ) {
          this.messageSeenAuthUser = messageSeen;
        }
        if (messageSeen.number > this.messageNumberSeen) {
          // test using this.messageNumberSeen NOT this.messageSeenAuthUser because #setRoughMessageNumberSeenInSetSeen
          this.messageNumberSeen = messageSeen.number;
          this.messageNumberSeenReplaySubject.next(messageSeen.number);
        }
      }
    });
    this.updateUnreadMessageCount();
    this.messageSeensReplaySubject.next(_.cloneDeep(this.messageSeens));
  }

  storeDraft({
    title,
    userIds,
    body,
    files,
  }: {
    title?: string;
    userIds?: string[];
    body: string;
    files: FileElement[];
  }) {
    this.draft = {
      title,
      userIds,
      body,
      files,
    };
  }

  createThread({
    message,
    title,
    userIds,
  }: {
    message?: { body: string; files?: FileElement[] };
    title: string;
    userIds: string[];
  }) {
    const messageSubject = new Subject<Message>();
    const threadSubject = new Subject<Thread>();
    const { orgId, refId: threadId, salt: threadSalt } = this;
    if (!threadSalt) throw new Error('salt not set');
    const path = generatePath<ThreadCreate.ReqParams>(
      '/api/orgs/:orgId/threads/:threadId',
      { orgId, threadId }
    );

    let postBody: ThreadCreate.ReqBody;

    if (message) {
      const messageSubmitted = this.generateMessageSubmitted(message);
      const { _id: messageId, salt, body, files } = messageSubmitted;
      postBody = {
        mode: 'thread',
        salt: threadSalt,
        userIds,
        title,
        message: { _id: messageId, salt, body, files },
      };
    } else {
      postBody = {
        mode: 'channel',
        salt: threadSalt,
        userIds,
        title,
      };
    }

    const userOrgObj = this.authUserService.getAuthUserOrgObj({ orgId });
    const authUserId = userOrgObj.user._id;
    const sub = this.http // No need to unsubscribe as not in a component
      .post<ThreadCreate.ResBody>(path, postBody)
      .subscribe({
        next: (result) => {
          this.update({
            thread: result.thread,
            messages: result.message ? [result.message] : undefined,
            messageSeens: result.messageSeen ? [result.messageSeen] : undefined,
          });
          const { inbox } = result;
          if (inbox)
            this.messageService.inboxSubject?.next({ inbox, authUserId });
          if (result.message) messageSubject.next(result.message);
          threadSubject.next(result.thread);
        },
        error: (error) => {
          messageSubject.error(error);
        },
      });
    this.subscriptions.add(sub);
    return {
      messageObservable: messageSubject.asObservable(),
      threadObservable: threadSubject.asObservable(),
    };
  }

  sendMessage(message: { body: string; files?: FileElement[] }): {
    messageSubmitted: MessageSubmitted;
    messageObservable: Observable<Message>;
  } {
    const messageSubject = new Subject<Message>();
    const messageSubmitted = this.generateMessageSubmitted(message);

    const { _id: messageId, salt, body, files } = messageSubmitted;
    const postBody: ThreadMessageSend.ReqBody = { salt, body, files };

    const { orgId } = this;
    const userOrgObj = this.authUserService.getAuthUserOrgObj({ orgId });
    const authUserId = userOrgObj.user._id;

    const queueItem = {
      messageSubmitted,
      messageObservable: messageSubject.asObservable(),
    };
    this.messageQueue.push(queueItem);

    this.messages.push(messageSubmitted);

    if (this.mode === 'task') {
      const { refId: taskId } = this;

      const sub = this.http // No need to unsubscribe as not in a component
        .post<TaskMessageSend.ResBody>(
          generatePath<TaskMessageSend.ReqParams>(
            '/api/orgs/:orgId/tasks/:taskId/messages/:messageId',
            { orgId, taskId, messageId }
          ),
          postBody
        )
        .subscribe({
          next: (result) => {
            this.taskService.storeServerTaskData({ task: result.task });

            this.update({
              task: result.task,
              messages: [result.message],
              messageSeens: result.messageSeen
                ? [result.messageSeen]
                : undefined,
            });

            // Remove from queue
            const queueIndex = this.messageQueue.indexOf(queueItem);
            if (queueIndex >= 0) this.messageQueue.splice(queueIndex, 1);

            const { inbox } = result;
            this.messageService.inboxSubject?.next({ inbox, authUserId });
            messageSubject.next(result.message);
            sub.unsubscribe();
          },
          error: (error) => {
            messageSubject.error(error);
            sub.unsubscribe();
          },
        });
      this.subscriptions.add(sub);
    } else {
      const { refId: threadId } = this;

      const sub = this.http // No need to unsubscribe as not in a component
        .post<ThreadMessageSend.ResBody>(
          generatePath<ThreadMessageSend.ReqParams>(
            '/api/orgs/:orgId/threads/:threadId/messages/:messageId',
            { orgId, threadId, messageId }
          ),
          postBody
        )
        .subscribe({
          next: (result) => {
            this.update({
              thread: result.thread,
              messages: [result.message],
              messageSeens: result.messageSeen
                ? [result.messageSeen]
                : undefined,
            });

            // Remove from queue
            const queueIndex = this.messageQueue.indexOf(queueItem);
            if (queueIndex >= 0) this.messageQueue.splice(queueIndex, 1);

            const { inbox } = result;
            if (inbox)
              this.messageService.inboxSubject?.next({ inbox, authUserId });
            messageSubject.next(result.message);
            sub.unsubscribe();
          },
          error: (error) => {
            messageSubject.error(error);
            sub.unsubscribe();
          },
        });
      this.subscriptions.add(sub);
    }

    return {
      messageSubmitted,
      messageObservable: messageSubject.asObservable(),
    };
  }

  generateMessageSubmitted({
    body,
    files,
  }: {
    body: string;
    files?: FileElement[];
  }): MessageSubmitted {
    const { salt, _id } = generateIdFromOrgId(this.orgId);

    const messageSubmitted: MessageSubmitted = {
      _id,
      salt,
      orgId: this.orgId,
      refId: this.refId,
      number: this.messageNumber + 1, // This is a guess to place it appropriately in the list of messages. It will be updated when submitted to the server.
      userId: this.authUserId,
      body,
      files: files?.length ? files : undefined,
      createdAt: Date.now(), // This is a guess. It will be updated when submitted to the server.
    };

    // this.messageQueue.push(messageSubmitted);
    return messageSubmitted;
  }

  updateTitle(title: string) {
    const updateSubject = new Subject<boolean>();
    const { orgId, refId: threadId } = this;
    const body: ThreadPatch.ReqBody = { title };
    const sub = this.http // No need to unsubscribe as not in a component
      .patch<ThreadPatch.ResBody>(
        generatePath<ThreadPatch.ReqParams>(
          '/api/orgs/:orgId/threads/:threadId',
          { orgId, threadId }
        ),
        body
      )
      .subscribe({
        next: (result) => {
          this.update({ thread: result.thread });
          updateSubject.next(true);
        },
        error: (error) => {
          updateSubject.error(error);
        },
      });
    this.subscriptions.add(sub);
    const updateObservable = updateSubject.asObservable();
    return updateObservable;
  }

  getMessages({
    start,
    end,
    limit,
  }: {
    start?: number;
    end?: number;
    limit: number;
  }) {
    let a: number;
    let b: number;
    if (typeof end === 'number' && typeof start === 'number') {
      a = start;
      b = end;
    } else if (typeof end === 'number') {
      a = end - (limit - 1);
      if (a < 0) a = 0;
      b = end;
    } else if (typeof start === 'number') {
      a = start;
      b = start + (limit - 1);
    } else {
      return this.getMessagesFromServer({ limit });
    }

    const messages = this.messages.filter(
      (m) => m.number >= a && m.number <= b
    );
    if (messages.length === b - a + 1)
      return new BehaviorSubject<Message[]>(messages);

    return this.getMessagesFromServer({ start, end, limit });
  }

  private getMessagesFromServer({
    start,
    end,
    limit,
  }: TaskMessagesGet.ReqQuery | ThreadMessagesGet.ReqQuery): BehaviorSubject<
    Message[]
  > {
    // Update the latest messages
    let params: HttpParams | undefined = new HttpParams();
    if (limit) params = params.append('limit', limit);
    if (typeof start === 'number') params = params.append('start', start);
    if (typeof end === 'number') params = params.append('end', end);

    const { orgId, refId } = this;
    const behaviorSubject = new BehaviorSubject<Message[]>([]);

    if (this.mode === 'task') {
      const sub = this.http
        .get<TaskMessagesGet.ResBody>(
          generatePath<TaskMessagesGet.ReqParams>(
            '/api/orgs/:orgId/tasks/:taskId/messages',
            { orgId, taskId: refId }
          ),
          { params }
        )
        .subscribe({
          next: (taskSummary) => {
            this.update(taskSummary);
            behaviorSubject.next(taskSummary.messages);
          },
          error: (err) => {
            if (err.status === 404) {
              behaviorSubject.next([]); // Usually this is because it is a new thread. TODO: check that the thread is a new task
              return;
            }
            behaviorSubject.error(err);
          },
        });
      this.subscriptions.add(sub);
    } else {
      const sub = this.http
        .get<ThreadMessagesGet.ResBody>(
          generatePath<ThreadMessagesGet.ReqParams>(
            '/api/orgs/:orgId/threads/:threadId/messages',
            { orgId, threadId: refId }
          ),
          { params }
        )
        .subscribe({
          next: ({ thread, messages, messageSeens }) => {
            if (thread) this.update({ thread, messages, messageSeens });
            behaviorSubject.next(messages);
          },
          error: (err) => {
            behaviorSubject.error(err);
          },
        });
      this.subscriptions.add(sub);
    }
    return behaviorSubject;
  }

  setSeen({ number }: { number: number }) {
    if (number > this.messageNumberSeen) {
      this.messageNumberSeen = number; // #setRoughMessageNumberSeenInSetSeen
      this.updateUnreadMessageCount();
      this.messageNumberSeenReplaySubject.next(number);
      this.postMessageNumberSeenSubject.next(number);
    }
  }

  addUser({ userId }: { userId: string }) {
    const { orgId, refId } = this;

    if (this.mode === 'task') {
      this.taskService.updateTask(
        { orgId, taskId: refId },
        { push: { userIds: [userId] } }
      );
    } else {
      const updateSubject = new Subject<boolean>();
      const body: ThreadPatch.ReqBody = { push: { userIds: [userId] } };
      const sub = this.http // No need to unsubscribe as not in a component
        .patch<ThreadPatch.ResBody>(
          generatePath<ThreadPatch.ReqParams>(
            '/api/orgs/:orgId/threads/:threadId',
            { orgId, threadId: refId }
          ),
          body
        )
        .subscribe({
          next: (result) => {
            this.update({ thread: result.thread });
            updateSubject.next(true);
          },
          error: (error) => {
            updateSubject.error(error);
          },
        });
      this.subscriptions.add(sub);
      const updateObservable = updateSubject.asObservable();
      return updateObservable;
    }
  }

  private postSeenToServer({ messageNumber }: { messageNumber: number }) {
    if (this.mode === 'task') {
      const { orgId, refId: taskId } = this;
      const req: TaskMessageSeenPost.ReqBody = {};
      const sub = this.http
        .post<TaskMessageSeenPost.ResBody>(
          generatePath<TaskMessageSeenPost.ReqParams>(
            '/api/orgs/:orgId/tasks/:taskId/messages/:messageNumber/seen',
            { orgId, taskId, messageNumber }
          ),
          req
        )
        .subscribe(({ messageSeen }) => {
          if (messageSeen) this.updateMessageSeens([messageSeen]);
        });
      this.subscriptions.add(sub);
    } else {
      const { orgId, refId: threadId } = this;
      const req: ThreadMessageSeenPost.ReqBody = {};
      const sub = this.http
        .post<ThreadMessageSeenPost.ResBody>(
          generatePath<ThreadMessageSeenPost.ReqParams>(
            '/api/orgs/:orgId/threads/:threadId/messages/:messageNumber/seen',
            { orgId, threadId, messageNumber }
          ),
          req
        )
        .subscribe(({ messageSeen }) => {
          if (messageSeen) this.updateMessageSeens([messageSeen]);
        });
      this.subscriptions.add(sub);
    }
  }

  cleanUp() {
    this.subscriptions.unsubscribe();
  }
}

@Injectable({ providedIn: 'root' })
export class MessageService {
  private http = inject(HttpClient);
  private authUserService = inject(AuthUserService);
  private taskService = inject(TaskService);
  private webSocketService = inject(WebSocketService);

  // The assumption is that all the threadItems in this array belong to a single
  // userId and must be cleaned up when changing users
  threadItems: ThreadItem[] = [];
  private typingSubject = new Subject<{
    refId: string;
    userId: string;
    isTyping: boolean;
    time: number;
  }>();
  typingObservable = this.typingSubject.asObservable();
  userOrgObj?: DeepReadonly<UserOrgObj>;
  latestRequest: { authUserId: string; time: number }[] = [];

  inboxSubject?: Subject<{ inbox: Inbox; authUserId: string }>;
  inboxDraftSubject?: Subject<{
    orgId: string;
    refId: string;
    inboxMode: 'thread' | 'task';
  }>;

  threadSubject = new Subject<Thread>();
  threadObservable = this.threadSubject.asObservable();

  constructor() {
    this.startWebSocket();

    this.authUserService.userOrgObj.subscribe((userOrgObj) => {
      // If there is an existing user and the new user is a different user,
      // remove all the threadItems belonging to the user
      if (
        this.userOrgObj &&
        (!userOrgObj || userOrgObj.user._id !== this.userOrgObj.user._id)
      ) {
        this.cleanUpThreadItems(this.userOrgObj.user._id);
      }
      this.userOrgObj = userOrgObj;
    });
  }

  setInboxSubject(inboxSubject: Subject<{ inbox: Inbox; authUserId: string }>) {
    this.inboxSubject = inboxSubject;
  }

  setInboxDraftSubject(
    draftSubject: Subject<{
      orgId: string;
      refId: string;
      inboxMode: 'thread' | 'task';
    }>
  ) {
    this.inboxDraftSubject = draftSubject;
  }

  private findExistingThreadItem({ refId }: { refId: string }) {
    return this.threadItems.find((t) => t.refId === refId);
  }

  newThread(mode: 'thread' | 'channel'): ThreadItem {
    if (!this.userOrgObj) throw new Error('userOrgObj not set');
    const orgId = this.userOrgObj.org._id;
    const threadItem = this.createThreadItem({ orgId, mode });
    return threadItem;
  }

  createThreadItem(
    input: {
      orgId: string;
    } & (
      | { refId: string; mode: ThreadOrTaskMode }
      | { refId?: undefined; mode: 'thread' | 'channel' }
    )
  ): ThreadItem {
    if (!this.userOrgObj) throw new Error('userOrgObj not set');

    if (this.userOrgObj.org._id !== input.orgId)
      throw new Error('orgId does not align in createThreadItem');
    const authUserId = this.userOrgObj.user._id;
    const threadItem = new ThreadItem(
      { authUserId, ...input },
      {
        http: this.http,
        authUserService: this.authUserService,
        taskService: this.taskService,
        messageService: this,
      }
    );
    this.threadItems.push(threadItem);
    return threadItem;
  }

  updateOrCreateThreadItem({
    authUserId,
    thread,
    task,
    inbox,
    messages = [],
    messageSeens,
  }: {
    authUserId: string;
    inbox?: Inbox;
    messages?: Message[];
    messageSeens?: MessageSeen[];
  } & (
    | { thread: Thread; task?: undefined }
    | { thread?: undefined; task: OrgTask }
  )): ThreadItem | undefined {
    // If it is not the logged userId
    if (!this.userOrgObj || this.userOrgObj.user._id !== authUserId) return;

    try {
      const { _id: refId, orgId } = thread ?? task;
      let threadItem = this.findExistingThreadItem({ refId });

      if (task) {
        if (!threadItem)
          threadItem = this.createThreadItem({ orgId, refId, mode: 'task' });
        threadItem.update({ task, messages, messageSeens });
        if (inbox) this.inboxSubject?.next({ inbox, authUserId });
      } else if (thread) {
        if (!threadItem)
          threadItem = this.createThreadItem({
            orgId,
            refId,
            mode: thread.mode,
          });
        threadItem.update({ thread, messages, messageSeens });
        if (inbox) this.inboxSubject?.next({ inbox, authUserId });
      }
    } catch (error) {
      console.error(error); // don't let a single thread failure blow up the rest of the updates
    }
  }

  private addWebSocketMessage({
    authUserId,
    message,
    thread: threadFromWebSocket,
    task: taskFromWebSocket,
    inbox,
    messageSeens,
  }: (
    | { thread: Thread; task?: undefined }
    | { thread?: undefined; task: OrgTask }
  ) & {
    authUserId: string;
    message: Message;
    inbox?: Inbox;
    messageSeens?: MessageSeen[];
  }): void {
    const threadOrTask = threadFromWebSocket
      ? { thread: threadFromWebSocket }
      : { task: taskFromWebSocket };
    this.updateOrCreateThreadItem({
      ...threadOrTask,
      authUserId,
      inbox,
      messages: [message],
      messageSeens,
    });

    // Make notifications
    // Don't wait for getThreadSummary etc to complete
    const messageFromOtherUser = message.userId !== authUserId;
    // TODO: determine if there is a mute
    if (
      messageFromOtherUser &&
      ((threadFromWebSocket &&
        threadFromWebSocket.mode !== 'channel' &&
        threadFromWebSocket.mode !== 'space') ||
        (taskFromWebSocket &&
          (taskFromWebSocket.userIds.includes(authUserId) ||
            taskFromWebSocket.assignedTo === authUserId)))
    ) {
      this.playAudio();
      // TODO: create overlay
    }
  }

  getThreadItem({
    orgId,
    refId,
    mode,
  }: {
    orgId: string;
    refId: string;
    mode: ThreadOrTaskMode;
  }): ThreadItem {
    const threadItem =
      this.findExistingThreadItem({ refId }) ??
      this.createThreadItem({ orgId, refId, mode });
    return threadItem;
  }

  playAudio(): void {
    const audio = new Audio();
    audio.src = '../../assets/audio/whistleronic.wav';
    audio.load();
    audio.play();
  }

  startWebSocket(): void {
    this.webSocketService.pushObservable.subscribe((socketResult) => {
      if (socketResult.service === 'message') {
        const { orgId, userId } = socketResult;
        if (
          // We are only watching WebSocket results of the current logged in user
          this.userOrgObj &&
          orgId === this.userOrgObj.org._id &&
          userId === this.userOrgObj.user._id
        ) {
          const authUserId = userId;
          if (socketResult.action === 'new' && socketResult.data.message) {
            this.addWebSocketMessage({
              ...socketResult.data,
              message: socketResult.data.message,
              authUserId,
            });
          } else if (socketResult.action === 'update') {
            this.updateOrCreateThreadItem({ ...socketResult.data, authUserId });
          } else if (socketResult.action === 'typing') {
            this.typingSubject.next(socketResult.data);
          } else if (socketResult.action === 'seen') {
            const messageSeen = socketResult.data;
            this.findThreadItemUpdateMessageSeen({ messageSeen });
          }
        }
      }
    });
  }

  private findThreadItemUpdateMessageSeen({
    messageSeen,
  }: {
    messageSeen: MessageSeen;
  }) {
    const { refId } = messageSeen;
    const threadItem = this.findExistingThreadItem({ refId });
    // if there is no threadItem, we probably aren't following the thread
    if (!threadItem) return;
    // If the seenMessage status exists for the users, replace it, otherwise add it
    threadItem.updateMessageSeens([messageSeen]);
  }

  deleteThread({ orgId, threadId }: { orgId: string; threadId: string }) {
    const subject = new Subject<boolean>();
    const path = generatePath<ThreadDelete.ReqParams>(
      '/api/orgs/:orgId/threads/:threadId',
      { orgId, threadId }
    );
    this.http.delete<ThreadDelete.ResBody>(path).subscribe({
      next: () => {
        // TODO: remove threadItem, update threadItems, update inbox
        subject.next(true);
      },
      error: (err) => {
        subject.error(err);
      },
    });
    return subject;
  }

  leave({ orgId, refId }: { orgId: string; refId: string }) {
    const threadItem = this.findExistingThreadItem({ refId });

    if (!threadItem) {
      console.error('threadItem not found');
      return;
    }

    const subject = new Subject<boolean>();
    const path =
      threadItem.mode === 'task'
        ? generatePath<TaskLeave.ReqParams>(
            '/api/orgs/:orgId/tasks/:taskId/leave',
            { orgId, taskId: refId }
          )
        : generatePath<ThreadLeave.ReqParams>(
            '/api/orgs/:orgId/threads/:threadId/leave',
            { orgId, threadId: refId }
          );

    this.http
      .post<
        | (ThreadLeave.ResBody & { task?: undefined })
        | (TaskLeave.ResBody & { thread?: undefined })
      >(path, {})
      .subscribe({
        next: ({ task, thread }) => {
          const { authUserId } = threadItem;
          if (thread) this.updateOrCreateThreadItem({ authUserId, thread });
          if (task) this.taskService.storeServerTaskData({ task });
          subject.next(true);
        },
        error: (err) => {
          subject.error(err);
        },
      });
    return subject;
  }

  private cleanUpThreadItems(authUserId: string) {
    const clean = this.threadItems.filter((t) => t.authUserId === authUserId);
    this.threadItems = this.threadItems.filter(
      (t) => t.authUserId !== authUserId
    );
    clean.forEach((threadItem) => threadItem.cleanUp());
  }
}
